import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import axiosClient from '../../../../api/axiosClient';
import SubPagesLayout from '../../../UI/layout/subPages';
import { courseMembersQuery } from '../../insights/api';
import { coursesQuery } from '../assignments/api';
import { navigation } from '../assignments/data';

import './styles.css';

// Parse a CSV file with headers `first_name,last_name,email` (email
// optional). Returns { rows, errors } — rows are only the valid ones,
// errors describe malformed / missing-required rows for surfacing to
// the instructor.
function parseRosterCsv(text) {
  const errors = [];
  const rows = [];

  const lines = text.replace(/\r\n/g, '\n').split('\n').map(l => l.trim()).filter(Boolean);
  if (!lines.length) {
    errors.push('The file is empty.');
    return { rows, errors };
  }

  const header = lines[0].toLowerCase().split(',').map(h => h.trim());
  const firstNameIdx = header.indexOf('first_name');
  const lastNameIdx = header.indexOf('last_name');
  const emailIdx = header.indexOf('email');

  if (firstNameIdx === -1 || lastNameIdx === -1) {
    errors.push('CSV must include `first_name` and `last_name` columns. Download the template and try again.');
    return { rows, errors };
  }

  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(',').map(c => c.trim());
    const first_name = cells[firstNameIdx] || '';
    const last_name = cells[lastNameIdx] || '';
    const email = emailIdx !== -1 ? (cells[emailIdx] || '') : '';

    if (!first_name || !last_name) {
      errors.push(`Row ${i + 1}: first_name and last_name are required.`);
      continue;
    }
    const record = { first_name, last_name };
    if (email) record.email = email;
    rows.push(record);
  }

  return { rows, errors };
}

async function bulkCreateStudents({ courseId, students }) {
  const { data } = await axiosClient.post('/api/users/students/bulk/', {
    course_id: courseId,
    students,
  });
  return data;
}

// The bulk endpoint can return several error shapes. Flatten them into
// a single human-readable string for the modal banner.
function formatUploadError(err) {
  const res = err?.response?.data;
  if (!res) return err?.message || 'Upload failed. Please try again.';

  if (typeof res === 'string') return res;
  if (res.detail) return String(res.detail);

  const parts = [];

  // course_id can be array-of-strings (unknown course) or a plain string
  // (cap-exceeded message).
  if (res.course_id) {
    if (Array.isArray(res.course_id)) parts.push(res.course_id.join(' '));
    else parts.push(String(res.course_id));
  }

  // students is an array parallel to the submitted rows; each entry is
  // either {} (valid) or { firstname: ["..."], email: ["..."], ... }.
  if (Array.isArray(res.students)) {
    res.students.forEach((rowErr, i) => {
      if (!rowErr || Object.keys(rowErr).length === 0) return;
      const rowMessages = Object.entries(rowErr)
        .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(' ') : msgs}`)
        .join('; ');
      parts.push(`Row ${i + 2}: ${rowMessages}`);
    });
  }

  return parts.length ? parts.join('\n') : 'Upload failed. Please try again.';
}

export default function InstructorSettingsRoster() {
  const { data: courseMembers, isLoading } = useQuery(courseMembersQuery());
  const { data: courses } = useQuery(coursesQuery());
  const queryClient = useQueryClient();

  const courseId = courses?.[0]?.id;

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  // When a parsed CSV has any row without an email we hold the parsed
  // rows here and show a warning view so the instructor can confirm
  // before we POST. Cleared once they either cancel or confirm.
  const [pendingRowsAwaitingConfirmation, setPendingRowsAwaitingConfirmation] = useState(null);
  const fileInputRef = useRef(null);

  // Uploads happen in sequential batches of 100 students so the backend
  // doesn't have to process a single 600-row payload (which risks timeouts
  // and gives no user feedback for a minute+). isPending / progress /
  // partial-result / error are managed manually here since useMutation
  // covers only one request at a time.
  const CHUNK_SIZE = 100;
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { uploaded, total }

  const uploadInChunks = async ({ courseId, students }) => {
    if (isUploading) return;
    setIsUploading(true);
    setUploadError(null);
    setUploadResult(null);
    setUploadProgress({ uploaded: 0, total: students.length });

    const combinedResults = [];
    let uploaded = 0;

    for (let i = 0; i < students.length; i += CHUNK_SIZE) {
      const chunk = students.slice(i, i + CHUNK_SIZE);
      try {
        const data = await bulkCreateStudents({ courseId, students: chunk });
        if (Array.isArray(data?.results)) {
          combinedResults.push(...data.results);
        }
        uploaded += chunk.length;
        setUploadProgress({ uploaded, total: students.length });
      } catch (err) {
        // Abort on first failure. Surface partial success so the
        // instructor knows which students made it in and can re-upload
        // the full CSV — the backend flags already-added rows as
        // duplicates, no double-adds.
        const detail = formatUploadError(err);
        setUploadError(
          `Uploaded ${uploaded} of ${students.length} students. The next batch failed: ${detail}`
        );
        if (combinedResults.length) {
          setUploadResult({ results: combinedResults });
        }
        queryClient.invalidateQueries({ queryKey: ['courseMembers'] });
        setIsUploading(false);
        setUploadProgress(null);
        return;
      }
    }

    queryClient.invalidateQueries({ queryKey: ['courseMembers'] });
    setUploadResult({ results: combinedResults });
    setIsUploading(false);
    setUploadProgress(null);
  };

  const students = useMemo(() => {
    if (!courseMembers) return [];
    return courseMembers
      .filter(member => member.is_student)
      .sort((a, b) => {
        const lastCompare = (a.last_name || '').localeCompare(b.last_name || '');
        if (lastCompare !== 0) return lastCompare;
        return (a.first_name || '').localeCompare(b.first_name || '');
      });
  }, [courseMembers]);

  const openAddModal = () => {
    setSelectedFile(null);
    setUploadError(null);
    setUploadResult(null);
    setPendingRowsAwaitingConfirmation(null);
    setIsAddModalOpen(true);
  };

  function closeAddModal() {
    setIsAddModalOpen(false);
    setSelectedFile(null);
    setUploadError(null);
    setUploadResult(null);
    setPendingRowsAwaitingConfirmation(null);
  }

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setUploadError(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadError(null);
    }
  };

  const handleTemplateDownload = (e) => {
    e.preventDefault();
    // first_name + last_name required; email optional. Header-only CSV
    // so the instructor sees the expected column names and order.
    const csv = 'first_name,last_name,email\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'roster-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    if (!courseId) {
      setUploadError('No course is available for this instructor account.');
      return;
    }
    setUploadError(null);

    let text;
    try {
      text = await selectedFile.text();
    } catch {
      setUploadError('Could not read the file. Please try again.');
      return;
    }

    const { rows, errors } = parseRosterCsv(text);
    if (errors.length) {
      setUploadError(errors.join('\n'));
      return;
    }
    if (!rows.length) {
      setUploadError('No student rows found in the file.');
      return;
    }

    // Gate on missing emails: show a confirmation view before POSTing
    // so the instructor understands the password-reset / duplicate-name
    // trade-off. Confirmed uploads flow through handleConfirmUpload.
    const hasMissingEmail = rows.some(r => !r.email);
    if (hasMissingEmail) {
      setPendingRowsAwaitingConfirmation(rows);
      return;
    }

    uploadInChunks({ courseId, students: rows });
  };

  const handleConfirmUpload = () => {
    if (!pendingRowsAwaitingConfirmation) return;
    const rows = pendingRowsAwaitingConfirmation;
    setPendingRowsAwaitingConfirmation(null);
    uploadInChunks({ courseId, students: rows });
  };

  const handleCancelUpload = () => {
    setPendingRowsAwaitingConfirmation(null);
  };

  // Rows for the "here are the accounts we generated" table on the
  // results view — we only care about students who had no email in
  // the CSV, since the instructor needs the backend-generated username
  // + default password to distribute to them.
  const generatedAccounts = useMemo(() => {
    if (!uploadResult?.results) return [];
    // The results array is parallel to the submitted students. Any row
    // whose returned email looks like the backend's <username>@example.com
    // placeholder is one we need to surface. This heuristic matches the
    // API notes ("Placeholder email — if email omitted...").
    return uploadResult.results.filter(r =>
      r?.user_created
      && typeof r.email === 'string'
      && r.email.endsWith('@example.com')
    );
  }, [uploadResult]);

  return (
    <SubPagesLayout
      navigation={navigation}
      contentClassName="adjustment-content-container"
    >
      <div className="instructor-roster">
        <div className="instructor-roster-header">
          <h2>Roster</h2>
          <button
            className="button-blue instructor-roster-add-button"
            type="button"
            onClick={openAddModal}
          >
            <span>+</span> Add Students
          </button>
        </div>

        <div className="instructor-roster-table-wrapper">
          <table className="instructor-roster-table">
            <thead>
              <tr>
                <th className="col-number">No.</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Username</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr className="instructor-roster-empty">
                  <td colSpan={5}>Loading roster&hellip;</td>
                </tr>
              )}
              {!isLoading && students.length === 0 && (
                <tr className="instructor-roster-empty">
                  <td colSpan={5}>No students yet. Click <b>Add Students</b> to upload your roster.</td>
                </tr>
              )}
              {!isLoading && students.map((student, index) => (
                <tr key={student.id}>
                  <td className="col-number">{index + 1}</td>
                  <td>{student.first_name || '—'}</td>
                  <td>{student.last_name || '—'}</td>
                  <td>{student.email || '—'}</td>
                  <td>{student.username || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isAddModalOpen && (
        <div className="add-students-overlay" onClick={closeAddModal}>
          <div className="add-students-modal" onClick={(e) => e.stopPropagation()}>
            {!uploadResult && !pendingRowsAwaitingConfirmation && (
              <>
                <h3 className="add-students-title">Add Students</h3>
                <p className="add-students-instructions">
                  Use <a href="#" className="add-students-template-link" onClick={handleTemplateDownload}>this template</a> to bulk add or modify teams.
                </p>

                <div
                  className="add-students-dropzone"
                  onClick={handleBrowseClick}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  role="button"
                  tabIndex={0}
                >
                  <div className="add-students-dropzone-icon" aria-hidden="true" />
                  <div className="add-students-dropzone-title">
                    Drop your CSV file here or click to browse
                  </div>
                  <div className="add-students-dropzone-subtitle">
                    Accepts .csv files exported from the template above
                  </div>
                </div>

                <div className="add-students-file-row">
                  <label className="add-students-choose-button">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                    />
                    Choose file
                  </label>
                  <span className="add-students-file-name">
                    {selectedFile ? selectedFile.name : 'No file chosen'}
                  </span>
                </div>

                {uploadError && (
                  <div className="add-students-error" role="alert">
                    {uploadError}
                  </div>
                )}

                {isUploading && (
                  <div className="add-students-progress" role="status" aria-live="polite">
                    <span className="add-students-progress-spinner" aria-hidden="true" />
                    <span>
                      {uploadProgress
                        ? `Uploading your roster… ${uploadProgress.uploaded} of ${uploadProgress.total} students done.`
                        : 'Uploading your roster… this can take up to 2 minutes for large classes.'}
                    </span>
                  </div>
                )}

                <div className="add-students-actions">
                  <button
                    type="button"
                    className="add-students-cancel"
                    onClick={closeAddModal}
                    disabled={isUploading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="add-students-upload"
                    onClick={handleUpload}
                    disabled={!selectedFile || isUploading}
                  >
                    {isUploading ? 'Uploading…' : 'Upload'}
                  </button>
                </div>
              </>
            )}

            {pendingRowsAwaitingConfirmation && !uploadResult && (
              <>
                <h3 className="add-students-title">Heads up: some students have no email</h3>
                <p className="add-students-instructions">
                  Your file is missing an email address for one or more students.
                  Emails aren&rsquo;t required, but without them:
                </p>
                <ul className="add-students-warning-list">
                  <li>You won&rsquo;t be able to reset those students&rsquo; passwords.</li>
                  <li>Grades can be hard to track if two students share the same first and last name.</li>
                </ul>
                <p className="add-students-instructions">
                  You can still upload now, or cancel and add the missing emails first.
                </p>

                {isUploading && (
                  <div className="add-students-progress" role="status" aria-live="polite">
                    <span className="add-students-progress-spinner" aria-hidden="true" />
                    <span>
                      {uploadProgress
                        ? `Uploading your roster… ${uploadProgress.uploaded} of ${uploadProgress.total} students done.`
                        : 'Uploading your roster… this can take up to 2 minutes for large classes.'}
                    </span>
                  </div>
                )}

                <div className="add-students-actions">
                  <button
                    type="button"
                    className="add-students-cancel"
                    onClick={handleCancelUpload}
                    disabled={isUploading}
                  >
                    Cancel upload
                  </button>
                  <button
                    type="button"
                    className="add-students-upload"
                    onClick={handleConfirmUpload}
                    disabled={isUploading}
                  >
                    {isUploading ? 'Uploading…' : 'Upload'}
                  </button>
                </div>
              </>
            )}

            {uploadResult && (
              <>
                <h3 className="add-students-title">Upload complete</h3>
                <p className="add-students-instructions">
                  <b>{uploadResult.added_count}</b> added, <b>{uploadResult.skipped_count}</b> already enrolled.
                </p>

                {generatedAccounts.length > 0 && (
                  <div className="add-students-generated">
                    <div className="add-students-generated-title">
                      Login info for students you added without an email
                    </div>
                    <div className="add-students-generated-subtitle">
                      Default password: <code>accounting101</code>. Share these usernames so they can sign in.
                    </div>
                    <div className="add-students-generated-table-wrapper">
                      <table className="add-students-generated-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Username</th>
                          </tr>
                        </thead>
                        <tbody>
                          {generatedAccounts.map(row => (
                            <tr key={row.id}>
                              <td>{row.first_name} {row.last_name}</td>
                              <td><code>{row.username}</code></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="add-students-actions">
                  <button
                    type="button"
                    className="add-students-upload"
                    onClick={closeAddModal}
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </SubPagesLayout>
  );
}
